/**
 * @voltxampere/presets — Layout helpers (Sprint 25 R2 lane 4D-A)
 *
 * Hand-authored preset layouts share a single pin-coordinate
 * pipeline: every component's body sits at a world-space `position`
 * (the viewBox center per `packages/ui/src/canvas/draw/component.ts`
 * `HALF_W=20, HALF_H=13`), and each terminal's world coordinate is
 * derived by applying the component's `rotation` to the pin's local
 * coordinate inside the symbol's 40×26 viewBox.
 *
 * Closes TS-008/4D — preset terminals were hardcoded `(0,0)` so the
 * renderer's `WireTool.findTerminalAt` only ever matched the origin.
 * Hand-authored layouts now compute the real pin world coords up
 * front and store them in `instance.terminals[*].position`, which
 * the wire snap and engine pipeline both consume.
 *
 * Engine zero-diff: helpers live in `@voltxampere/presets`. The
 * engine reads `terminals[].netId` regardless of `position` so
 * sentinel circuits (engine's own in-test fixtures) are not
 * affected by this change.
 *
 * Pin local-coordinate table mirrors the UI symbol library
 * (`packages/ui/src/symbols/*.ts` `terminals` arrays). The table is
 * duplicated here intentionally so the presets package stays
 * decoupled from `@voltxampere/ui` (presets ship without a UI
 * dependency by design).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * UI symbol viewBox half-extents — every Sprint 10.1+ symbol uses
 * `40 × 26` so the body centers at `(20, 13)`. Component
 * `position` in world coordinates corresponds to this center.
 */
const VIEWBOX_CENTER_X = 20;
const VIEWBOX_CENTER_Y = 13;
/**
 * Pin local coordinates per component type. Mirrors UI symbol
 * library terminal arrays; keys are the canonical terminal ids
 * preset authors use in `makeComponent({ terminals: { ... } })`.
 *
 * IMPORTANT: when a UI symbol gains/loses a terminal, this table
 * must be updated to match. The symbol-id alias map
 * (`packages/ui/src/symbols/aliases.ts`) handles type-name
 * resolution; this table covers terminal-id resolution.
 */
export const PIN_LOCAL_BY_TYPE = Object.freeze({
    // Sources
    "voltage-source": Object.freeze({
        pos: Object.freeze({ x: 20, y: 3 }),
        neg: Object.freeze({ x: 20, y: 23 }),
    }),
    "dc-source": Object.freeze({
        // Mirror of voltage-source for the UI alias.
        pos: Object.freeze({ x: 20, y: 3 }),
        neg: Object.freeze({ x: 20, y: 23 }),
    }),
    "ac-source": Object.freeze({
        out: Object.freeze({ x: 20, y: 3 }),
        gnd: Object.freeze({ x: 20, y: 23 }),
    }),
    "pulse-source": Object.freeze({
        out: Object.freeze({ x: 0, y: 13 }),
        gnd: Object.freeze({ x: 40, y: 13 }),
    }),
    "current-source": Object.freeze({
        // Same convention as voltage-source until Lane 4b ships its
        // dedicated UI symbol; preset authors may now reference it.
        pos: Object.freeze({ x: 20, y: 3 }),
        neg: Object.freeze({ x: 20, y: 23 }),
    }),
    // Passive
    resistor: Object.freeze({
        p1: Object.freeze({ x: 0, y: 13 }),
        p2: Object.freeze({ x: 40, y: 13 }),
    }),
    capacitor: Object.freeze({
        p1: Object.freeze({ x: 0, y: 13 }),
        p2: Object.freeze({ x: 40, y: 13 }),
    }),
    inductor: Object.freeze({
        p1: Object.freeze({ x: 0, y: 13 }),
        p2: Object.freeze({ x: 40, y: 13 }),
    }),
    switch: Object.freeze({
        p1: Object.freeze({ x: 0, y: 13 }),
        p2: Object.freeze({ x: 40, y: 13 }),
    }),
    potentiometer: Object.freeze({
        p1: Object.freeze({ x: 0, y: 13 }),
        p2: Object.freeze({ x: 40, y: 13 }),
        w: Object.freeze({ x: 24, y: 25 }),
    }),
    // Connectors
    ground: Object.freeze({
        p: Object.freeze({ x: 20, y: 4 }),
    }),
    "wire-node": Object.freeze({
        // 4-pin junction (compass cross). Sprint 25 R3 lane 6 aligned
        // these with the schema canonical n/s/e/w terminal ids
        // (`packages/components/src/types/wire-node.ts:18-22`); the
        // legacy p1-p4 aliases stay for backward compatibility with
        // any preset author who used them.
        n: Object.freeze({ x: 20, y: 0 }),
        s: Object.freeze({ x: 20, y: 26 }),
        e: Object.freeze({ x: 40, y: 13 }),
        w: Object.freeze({ x: 0, y: 13 }),
        // Legacy aliases (Lane 4D-A).
        p1: Object.freeze({ x: 0, y: 13 }),
        p2: Object.freeze({ x: 40, y: 13 }),
        p3: Object.freeze({ x: 20, y: 0 }),
        p4: Object.freeze({ x: 20, y: 26 }),
    }),
    // Active 2-pin — both UI shorthand (A/K) and schema canonical
    // (anode/cathode) terminal ids must resolve to the same pin
    // local coords.
    diode: Object.freeze({
        A: Object.freeze({ x: 0, y: 13 }),
        K: Object.freeze({ x: 40, y: 13 }),
        anode: Object.freeze({ x: 0, y: 13 }),
        cathode: Object.freeze({ x: 40, y: 13 }),
    }),
    led: Object.freeze({
        A: Object.freeze({ x: 0, y: 13 }),
        K: Object.freeze({ x: 40, y: 13 }),
        anode: Object.freeze({ x: 0, y: 13 }),
        cathode: Object.freeze({ x: 40, y: 13 }),
    }),
    zener: Object.freeze({
        A: Object.freeze({ x: 0, y: 13 }),
        K: Object.freeze({ x: 40, y: 13 }),
        anode: Object.freeze({ x: 0, y: 13 }),
        cathode: Object.freeze({ x: 40, y: 13 }),
    }),
    // Active 3-pin BJT — UI shorthand (B/C/E) + schema canonical
    // (base/collector/emitter)
    "bjt-npn": Object.freeze({
        B: Object.freeze({ x: 10, y: 13 }),
        C: Object.freeze({ x: 32, y: 4 }),
        E: Object.freeze({ x: 32, y: 22 }),
        base: Object.freeze({ x: 10, y: 13 }),
        collector: Object.freeze({ x: 32, y: 4 }),
        emitter: Object.freeze({ x: 32, y: 22 }),
    }),
    "bjt-pnp": Object.freeze({
        B: Object.freeze({ x: 10, y: 13 }),
        E: Object.freeze({ x: 32, y: 4 }),
        C: Object.freeze({ x: 32, y: 22 }),
        base: Object.freeze({ x: 10, y: 13 }),
        emitter: Object.freeze({ x: 32, y: 4 }),
        collector: Object.freeze({ x: 32, y: 22 }),
    }),
    // Active 3-pin MOSFET — UI shorthand (G/D/S) + schema canonical
    // (gate/drain/source)
    "mosfet-nmos": Object.freeze({
        G: Object.freeze({ x: 0, y: 13 }),
        D: Object.freeze({ x: 32, y: 3 }),
        S: Object.freeze({ x: 32, y: 23 }),
        gate: Object.freeze({ x: 0, y: 13 }),
        drain: Object.freeze({ x: 32, y: 3 }),
        source: Object.freeze({ x: 32, y: 23 }),
    }),
    "mosfet-pmos": Object.freeze({
        G: Object.freeze({ x: 0, y: 13 }),
        S: Object.freeze({ x: 32, y: 3 }),
        D: Object.freeze({ x: 32, y: 23 }),
        gate: Object.freeze({ x: 0, y: 13 }),
        source: Object.freeze({ x: 32, y: 3 }),
        drain: Object.freeze({ x: 32, y: 23 }),
    }),
    // Active 3-pin JFET (Sprint 23 split shares the single UI symbol)
    "jfet-n": Object.freeze({
        G: Object.freeze({ x: 10, y: 13 }),
        D: Object.freeze({ x: 30, y: 4 }),
        S: Object.freeze({ x: 30, y: 22 }),
        gate: Object.freeze({ x: 10, y: 13 }),
        drain: Object.freeze({ x: 30, y: 4 }),
        source: Object.freeze({ x: 30, y: 22 }),
    }),
    "jfet-p": Object.freeze({
        G: Object.freeze({ x: 10, y: 13 }),
        D: Object.freeze({ x: 30, y: 4 }),
        S: Object.freeze({ x: 30, y: 22 }),
        gate: Object.freeze({ x: 10, y: 13 }),
        drain: Object.freeze({ x: 30, y: 4 }),
        source: Object.freeze({ x: 30, y: 22 }),
    }),
    // Op-amp — UI shorthand (in-, in+) + schema canonical
    // (in_neg, in_pos)
    opamp: Object.freeze({
        "in-": Object.freeze({ x: 0, y: 9 }),
        "in+": Object.freeze({ x: 0, y: 17 }),
        out: Object.freeze({ x: 40, y: 13 }),
        in_neg: Object.freeze({ x: 0, y: 9 }),
        in_pos: Object.freeze({ x: 0, y: 17 }),
    }),
});
/**
 * Compute the world coordinate of a single pin on a component
 * placed at `position` with the given `rotation`.
 *
 * The transform mirrors v1's `pinWorldXY` (`~/Desktop/VoltXAmpere/
 * src/interaction/pin-snap.js:8-15`) — rotation is in degrees,
 * 0/90/180/270 only per `RotationSchema`. Pin local coords are in
 * the symbol's 40×26 viewBox; we shift by the viewBox center so
 * `position` (the body center in world units) aligns with `(20,
 * 13)` local.
 */
export function pinWorldCoord(componentPosition, componentRotation, pinLocal) {
    const dx = pinLocal.x - VIEWBOX_CENTER_X;
    const dy = pinLocal.y - VIEWBOX_CENTER_Y;
    if (componentRotation === 0) {
        return { x: componentPosition.x + dx, y: componentPosition.y + dy };
    }
    const rad = (componentRotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return {
        x: componentPosition.x + dx * cos - dy * sin,
        y: componentPosition.y + dx * sin + dy * cos,
    };
}
/**
 * Lookup helper. Returns the pin local coord for a `(type,
 * terminalId)` pair, or `undefined` when the table has no entry.
 *
 * Preset authors call this through `makeComponent` indirectly —
 * the builder uses it to populate `terminals[*].position` from
 * the simpler `terminals: { id: netId }` map. Direct callers
 * (per-preset wire path computation) may use it to derive
 * endpoints for `wires[*].path`.
 */
export function pinLocalForTerminal(type, terminalId) {
    const pinMap = PIN_LOCAL_BY_TYPE[type];
    if (pinMap === undefined)
        return undefined;
    return pinMap[terminalId];
}
export function pinWorldFor(components, componentId, terminalId) {
    const comp = components.find((c) => c.id === componentId);
    if (comp === undefined) {
        throw new Error(`pinWorldFor: component "${componentId}" not in placement list`);
    }
    const local = pinLocalForTerminal(comp.type, terminalId);
    if (local === undefined) {
        throw new Error(`pinWorldFor: pin "${terminalId}" not registered for type "${comp.type}"`);
    }
    return pinWorldCoord(comp.position, comp.rotation, local);
}
//# sourceMappingURL=_layout-helpers.js.map